import React, { useEffect, useRef, useState, useLayoutEffect } from 'react';
import I18n from 'i18n-js';
import PropTypes from 'prop-types';
import useDeepCompareEffect from 'use-deep-compare-effect';
import { ApolloProvider, useQuery } from '@apollo/client';
import { QueryClientProvider } from '@tanstack/react-query';
import apolloClient from 'common/ApolloClient';
import Spinner from 'common/Spinner';
import videojs from 'video.js';
import QualitySelector from '@silvermine/videojs-quality-selector';
import { AudioPlayerProvider, useAudioPlayer } from 'react-use-audio-player';
import { GET_PRESENT_MODE_SLIDE_SHOW } from '../Utils/slideShowGraphQL';
import Slide from '../Slide';
import TeacherConnectionPopup from '../../../student/TeacherConnectionPopup';
import { logLiveSessionEvent } from '../../../../modules/liveSessionEvents';
import styles from './PresentMode.module.scss';
import './PresentMode.scss';
import Navigator from './Navigator';
import TransitionSlide from './TransitionSlide/index.tsx';
import {
  autoplayMediaSetting,
  formatPresentSlides,
  slideBackgroundAudioUrl,
  uniqueUrl,
  useSlugParser
} from './Utils.ts';
import useSlideConditions from './useSlideConditions';
import useInitialSlide from './useInitialSlide';
import useLiveSessionForensics from './useLiveSessionForensics';
import useSessionNavigationLock from './useSessionNavigationLock';
import useStudentSlideSync from './useStudentSlideSync';
import useTeacherSlideSync from './useTeacherSlideSync';
import 'video.js/dist/video-js.css';
import '@silvermine/videojs-quality-selector/dist/css/quality-selector.css';
import useSlideShowStore from '../stores/useSlideShowStore';
import useSlideShowContext from '../stores/useSlideShowContext';
import SlideShowStoreProvider from '../stores/SlideShowStoreProvider';
import useCurrentSlideShowStore from '../../../../stores/useCurrentSlideShowStore';
import { queryClient } from '../../../../utils/ReactQuery';
import useFlags from '../../../../hooks/useFlags';
import { SLIDE_SETTINGS_STORAGE_KEY } from './Constants';
import useThemes from '../../../../hooks/api/useThemes';
import cn from '../../../../utils/cn';

export const DEFAULT_BACKGROUND_AUDIO_VOLUME = 0.7;

const PresentModeWrapper = props => (
  <ApolloProvider client={apolloClient}>
    <SlideShowStoreProvider props={props}>
      <QueryClientProvider client={queryClient}>
        <AudioPlayerProvider>
          <PresentMode {...props} />
        </AudioPlayerProvider>
      </QueryClientProvider>
    </SlideShowStoreProvider>
  </ApolloProvider>
);

const PresentMode = (props) => {
  const [slides, setSlides] = useState([]);
  const slideRef = useRef();
  const [selectedSlide, setSelectedSlide] = useState(null);
  const [position, setPosition] = useState(0);
  const [isBackgroundAudioMuted, setIsBackgroundAudioMuted] = useState(false);
  const [themeClass, setThemeClass] = useState('');

  const setActivityType = useSlideShowContext(state => state.setActivityType);
  const setIsPresentMode = useSlideShowContext(state => state.setIsPresentMode);
  const setIsPresentModeStore = useSlideShowStore(state => state.setIsPresentMode);
  const setSlideRef = useSlideShowContext(state => state.setSlideRef);
  const setLocale = useSlideShowContext(state => state.setLocale);
  const setJumpToSlide = useSlideShowContext(state => state.setJumpToSlide);
  const setCurrentSlide = useSlideShowContext(state => state.setCurrentSlide);
  const unansweredQuestions = useSlideShowContext(state => state.unansweredQuestions);
  const setUnansweredQuestions = useSlideShowContext(state => state.setUnansweredQuestions);
  const setLessonId = useSlideShowContext(state => state.setLessonId);
  const setSlideShowId = useSlideShowContext(state => state.setSlideShowId);
  const setUnitId = useSlideShowContext(state => state.setUnitId);
  const themeId = useSlideShowContext(state => state.themeId);
  const setThemes = useSlideShowContext(state => state.setThemes);
  const activityType = useSlideShowContext(state => state.activityType);
  const setUserType = useSlideShowStore(state => state.setUserType);
  const setSlideShowTotalScore = useSlideShowContext(state => state.setSlideShowTotalScore);
  const activeSessionId = useSlideShowContext(state => state.activeSessionId);
  const setActiveSessionId = useSlideShowContext(state => state.setActiveSessionId);
  const setClassroomConflictSessionActive = useSlideShowContext(state => state.setClassroomConflictSessionActive);
  const setOwnActiveSessionElsewhere = useSlideShowContext(state => state.setOwnActiveSessionElsewhere);
  const isStudent = props.userType === 'Student';
  const setCurrentSlideShowId = useCurrentSlideShowStore(state => state.setCurrentSlideShowId);
  const setCurrentSlideId = useCurrentSlideShowStore(state => state.setCurrentSlideId);

  const { data: themes, isSuccess, isLoading } = useThemes();

  useEffect(() => {
    if (isLoading || !isSuccess) return;
    setThemes(themes);
  }, [isLoading, isSuccess, themes]);

  useEffect(() => {
    if (!themes) return;
    setThemeClass(themes.find(theme => theme.id === themeId)?.css_name);
  }, [themes, themeId]);

  useEffect(() => setActivityType(props.slideShow.activity_type), [props.slideShow.activity_type]);
  useEffect(() => {
    setIsPresentMode(true);
    // this is here because we apparently look at the store in `AnswerKeyButton.tsx` from drag and drop questions
    setIsPresentModeStore(true);
    setUserType(props.userType);
  }, []);
  useEffect(() => setSlideRef(slideRef), [slideRef.current]);
  useEffect(() => setLocale(I18n.locale), [I18n.locale]);
  useEffect(() => setActiveSessionId(props.activeSessionId), [props.activeSessionId]);
  useEffect(
    () => setClassroomConflictSessionActive(Boolean(props.classroomConflictSessionActive)),
    [props.classroomConflictSessionActive],
  );
  useEffect(
    () => setOwnActiveSessionElsewhere(Boolean(props.ownActiveSessionElsewhere)),
    [props.ownActiveSessionElsewhere],
  );
  // AIDEV-NOTE: Publishes the current slideshow id to a layout-level store so
  // StudentSessionListener can suppress the started popup when a join_notification targets
  // the slideshow the student is already viewing — useStudentSlideSync handles the lock.
  useEffect(() => {
    setCurrentSlideShowId(Number(props.slideShowId));
    return () => setCurrentSlideShowId(null);
  }, [props.slideShowId, setCurrentSlideShowId]);
  useDeepCompareEffect(() => setCurrentSlide(selectedSlide), [selectedSlide || {}]);

  // AIDEV-NOTE: Publishes the current slide id to the layout-level store so Rails-mounted islands
  // (e.g. the Drawing modal) outside SlideShowStoreProvider can subscribe and auto-close on slide change.
  useEffect(() => {
    setCurrentSlideId(selectedSlide?.id ? Number(selectedSlide.id) : null);

    return () => setCurrentSlideId(null);
  }, [selectedSlide?.id, setCurrentSlideId]);

  // need to move this one still
  const setGetSetSlideAudioVolume = useSlideShowStore(state => state.setGetSetSlideAudioVolume);
  // moving this one breaks a bunch of stuff
  const setSlideShow = useSlideShowStore(state => state.setSlideShow);
  const setStoreSlides = useSlideShowStore(state => state.setSlides);
  const setContextSlides = useSlideShowContext(state => state.setSlides);

  const queryData = {
    variables: {
      locale: props.locale,
      parentId: props.parentId,
      parentType: props.parentType,
      programId: props.programId,
      slideShowId: props.slideShowId,
      studentId: props.userType === 'Student' ? props.userId : null,
    },
  };

  const {
    loading: loadingSlideShow,
    data: slideShowData,
    error
  } = useQuery(GET_PRESENT_MODE_SLIDE_SHOW, queryData);
  window.videojs = videojs;
  QualitySelector(videojs);

  useSlugParser(selectedSlide);

  const { isSlideLocked } = useSlideConditions();
  const { isNavigationLocked } = useSessionNavigationLock();

  useDeepCompareEffect(() => {
    if (!slideShowData) return;
    if (!slideShowData.slideShow) return;
    if (!slideShowData.slideShow.lesson) return;

    setLessonId(slideShowData.slideShow.lesson.id);
  }, [slideShowData || {}]);

  useDeepCompareEffect(() => {
    if (!slideShowData) return;

    setSlideShowTotalScore(slideShowData.slideShow?.slideShowTotalScore || 0);
    setSlideShow(slideShowData.slideShow);
    setSlideShowId(slideShowData.slideShow.id);
  }, [slideShowData || {}]);

  useDeepCompareEffect(() => {
    if (!slideShowData) return;
    if (!slideShowData.slideShow.slides) return;

    setStoreSlides(formatPresentSlides(slideShowData.slideShow, props.slideIds, props.showTransitionSlides));
    setContextSlides(formatPresentSlides(slideShowData.slideShow, props.slideIds, props.showTransitionSlides));
  }, [slideShowData || {}]);

  useDeepCompareEffect(() => {
    if (!slideShowData) return;
    if (!slideShowData.slideShow) return;
    if (!slideShowData.slideShow.unit) return;

    setUnitId(slideShowData.slideShow.unit.id);
  }, [slideShowData || {}]);

  useDeepCompareEffect(() => {
    if (!slideShowData) return;
    if (!slideShowData.slideShow) return;
    if (!slideShowData.slideShow.unansweredQuestions) return;

    setUnansweredQuestions(slideShowData.slideShow.unansweredQuestions);
  }, [slideShowData || {}]);

  useLayoutEffect(() => {
    // Remove width and height styling from video to allow auto resizing to fit container for fullscreen.
    document.querySelectorAll('.vjs-tech').forEach((video) => {
      video.style.removeProperty('width');
      video.style.removeProperty('height');
    });
  }, [selectedSlide]);

  const audioPlayer = useAudioPlayer();

  const { autoplayAudio, teacherPacedMode } = useFlags();
  const {
    load: loadAudio,
    stop: stopAudio,
    playing: audioPlaying,
    volume: audioVolume,
  } = audioPlayer;

  const volumeForAudioPlayer = (isMuted) => {
    const slideSettings = JSON.parse(localStorage.getItem(SLIDE_SETTINGS_STORAGE_KEY)) || {};
    const { backgroundAudioVolume } = slideSettings;

    if (isMuted) return 0;

    return backgroundAudioVolume != null ?
             backgroundAudioVolume : audioVolume() !== 0 ?
             audioVolume() : DEFAULT_BACKGROUND_AUDIO_VOLUME;
  };

  useEffect(() => {
    if (audioPlaying) stopAudio();
    if (!autoplayAudio) return;

    const backgroundAudioUrl = slideBackgroundAudioUrl(selectedSlide);
    if (backgroundAudioUrl) {
      const isStudentInLiveSession = teacherPacedMode && isStudent && !!activeSessionId;
      const isMuted = !autoplayMediaSetting(props.slideShowId) || isStudentInLiveSession;
      setIsBackgroundAudioMuted(isMuted);

      // Use a unique version of the URL, to fix the case where
      // leaving a slide and coming back doesn't replay audio since it's the same `src` URL.
      const audioUrl = uniqueUrl(backgroundAudioUrl);

      loadAudio({
        autoplay: true,
        src: audioUrl,
        volume: volumeForAudioPlayer(isMuted)
      });
    }
  }, [selectedSlide, props.slideShowId, activeSessionId]);

  // store the method that sets and gets the audio volume
  useEffect(() => {
    setGetSetSlideAudioVolume(audioVolume);
  }, [audioPlaying, selectedSlide]);

  useEffect(() => {
    if (error) {
      // eslint-disable-next-line no-console
      console.error(error);
      return;
    }

    if (loadingSlideShow) return;
    if (!slideShowData || !slideShowData.slideShow.slides) return;

    setSlides(formatPresentSlides(slideShowData.slideShow, props.slideIds, props.showTransitionSlides));
  }, [slideShowData]);

  useInitialSlide({
    isSlideLocked,
    onSelectSlide: setSelectedSlide,
    selectedSlide,
    slides,
    startingSlide: props.startingSlide,
  });

  useEffect(() => {
    const applyCORSTimeout = setTimeout(() => {
      document.querySelectorAll('.vjs-poster').forEach((poster) => {
        // eslint-disable-next-line no-param-reassign
        poster.style.backgroundColor = 'transparent';
        poster.setAttribute('crossorigin', 'anonymous');
      });
    }, 1000);

    if (selectedSlide) setPosition(selectedSlide.position);

    return () => clearTimeout(applyCORSTimeout);
  }, [selectedSlide]);

  const jumpToSlide = (newPosition) => {
    if (isNavigationLocked) return;

    const slide = slides.at(newPosition);

    if (isSlideLocked(slide)) return;

    setSelectedSlide(slide);
  };

  useDeepCompareEffect(() => setJumpToSlide(jumpToSlide), [slides, unansweredQuestions, isNavigationLocked]);

  const pendingSyncRef = useRef(null);

  // PLAT-17273: matches by (id, type) since Slide.id and SlideGroup.id share an integer namespace.
  // Real slides have various STI types ('ContentSlide', etc.) — match anything non-'Transition'.
  const findSyncedSlide = (slideId, slideType) => slides.find((s) => {
    const sameId = Number(s.id) === Number(slideId);
    const matchesType = slideType === 'Transition' ? s.type === 'Transition' : s.type !== 'Transition';
    return sameId && matchesType;
  });

  const forceJumpToSlide = (slideId, slideType) => {
    if (slideId == null || slideType == null) return;

    const slide = findSyncedSlide(slideId, slideType);
    if (slide) {
      setSelectedSlide(slide);
      pendingSyncRef.current = null;
    } else {
      pendingSyncRef.current = { slideId, slideType };
      logLiveSessionEvent(activeSessionId, 'nav_unresolved', {
        slide_id: slideId,
        slide_type: slideType,
        slides_loaded: slides.length > 0,
      });
    }
  };

  // If slides haven't loaded yet (e.g. late-join transmit arrives before GraphQL resolves),
  // the target is buffered in pendingSyncRef and applied when slides become available.
  useEffect(() => {
    if (slides.length === 0) return;
    if (!pendingSyncRef.current) return;

    const { slideId, slideType } = pendingSyncRef.current;
    const slide = findSyncedSlide(slideId, slideType);
    if (slide) {
      setSelectedSlide(slide);
      pendingSyncRef.current = null;
    }
  }, [slides]);

  useTeacherSlideSync(selectedSlide, props.userType);
  const { closeConnectionPopup, connectionPopup } = useStudentSlideSync({
    activeSessionId,
    forceJumpToSlide,
    slideShowId: props.slideShowId,
    userType: props.userType,
  });
  useLiveSessionForensics({
    activeSessionId,
    forensicsSessionId: props.forensicsSessionId,
    hydratedSessionId: props.activeSessionId,
    selectedSlide,
    slideShowId: props.slideShowId,
    startingSlide: props.startingSlide,
    userType: props.userType,
  });

  const sharedProps = {
    parentId: props.parentId,
    parentType: props.parentType,
    programId: props.programId,
    slideShowId: props.slideShowId,
  };

  const displaySelectedSlide = (slide) => {
    const templateObjects = slide.slideTemplate && slide.slideTemplate.slideTemplateObjects;
    const slideGroups = slides.filter(s => s.type === 'Transition');

    return (
      <span className={`current-slide locale-${props.locale || 'en'}`} ref={slideRef}>
        {slide.type === 'Transition' ? (
          <TransitionSlide
            imageUrl={slideShowData.slideShow.imageUrl}
            slideGroupIndex={slideGroups.findIndex(group => group.id === slide.id)}
            slideGroups={slideGroups}
            jumpToSlide={jumpToSlide}
            AssignmentShortcutProps={props.AssignmentShortcutProps}
            LmsShareProps={props.LmsShareProps}
            GradeShortcutProps={props.GradeShortcutProps}
          />
        ) : (
          <Slide
            forDisplay="present"
            imageModel={slide.imageModel}
            slide={slide}
            slideId={slide.id}
            slideObjects={slide.slideObjects}
            templateObjects={templateObjects}
            userType={props.userType}
            jumpToSlide={jumpToSlide}
            slides={slides}
            slideGroups={slideGroups}
          />
        )}
        <Navigator
          FullScreenProps={{ fullScreenRef: { current: document.body } }}
          NavigationProps={{
            dropdownItemText: 'Slide',
            numElements: slides.length,
            onBack: () => jumpToSlide(position - 2),
            onDirectNav: jumpToSlide,
            onForward: () => jumpToSlide(position),
            position,
          }}
          BackToButtonProps={{
            ...sharedProps,
            disabled: activityType === 'marketing',
          }}
          SlideAudioControlProps={{
            isMuted: isBackgroundAudioMuted,
            setIsMuted: setIsBackgroundAudioMuted
          }}
          SlideShowEditButtonProps={{ ...sharedProps, position }}
          slideShow={props.slideShow}
        />
      </span>
    );
  };

  return (
    <div
      className={cn(styles.presentModeContainer, {
        [themeClass]: !!themeId
      })}
      id="slide_reel"
    >
      {loadingSlideShow ? <Spinner className="tw-flex tw-justify-center tw-items-center tw-text-white" /> : (
        slides && selectedSlide && displaySelectedSlide(selectedSlide)
      )}
      {teacherPacedMode && props.userType === 'Student' && (
        <TeacherConnectionPopup
          variant={connectionPopup}
          onClose={closeConnectionPopup}
        />
      )}
    </div>
  );
};

PresentMode.propTypes = {
  activeSessionId: PropTypes.string,
  forensicsSessionId: PropTypes.string,
  allowImpersonate: PropTypes.bool.isRequired,
  allowViewSlideshows: PropTypes.bool,
  classroomConflictSessionActive: PropTypes.bool,
  environment: PropTypes.string.isRequired,
  ownActiveSessionElsewhere: PropTypes.bool,
  isResettable: PropTypes.bool,
  hasNotebookQuestions: PropTypes.bool,
  locale: PropTypes.string,
  AssignmentShortcutProps: PropTypes.object.isRequired,
  GradeShortcutProps: PropTypes.object.isRequired,
  LmsShareProps: PropTypes.object.isRequired,
  parentId: PropTypes.string,
  parentType: PropTypes.string,
  programId: PropTypes.number.isRequired,
  referrerProgramId: PropTypes.number,
  showTransitionSlides: PropTypes.bool,
  slideIds: PropTypes.arrayOf(PropTypes.number),
  slideShow: PropTypes.object.isRequired,
  slideShowId: PropTypes.number.isRequired,
  startingSlide: PropTypes.number,
  themeId: PropTypes.number,
  userId: PropTypes.number.isRequired,
  userType: PropTypes.string.isRequired,
};

PresentMode.defaultProps = {
  allowViewSlideshows: false,
  isResettable: false,
  hasNotebookQuestions: false,
  locale: 'en',
  parentId: null,
  parentType: null,
  showTransitionSlides: true,
  slideIds: [],
  startingSlide: 1,
  themeId: null,
};

export default PresentModeWrapper;
